"use client";

/**
 * VideoOnboardingModal — the auto-playing Mux walkthrough that greets a
 * new user right after the signup survey.
 *
 * Replaces the legacy 6-step `OnboardingTourHub` checklist (which never
 * covered Skills / Signals and had gone stale). Deliberately dead simple:
 * pop a modal, auto-play the onboarding video, let the user dismiss it any
 * time (X / outside click / Escape / "Don't show this again" — ALL of them
 * persist the dismissal to the DB via the gate's onClose). The same video
 * lives highlighted as the first card in the Tutorials tab.
 * 
 * Uses Mux Player for fast, reliable embed (no YouTube bot detection).
 * Falls back to YouTube link if user prefers.
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getOnboardingVideo } from "@/lib/videos-catalog";
import { ExternalLink } from "lucide-react";

interface VideoOnboardingModalProps {
  open: boolean;
  onClose: (reason?: "closed" | "dont_show_again") => void;
}

export function VideoOnboardingModal({ open, onClose }: VideoOnboardingModalProps) {
  const video = getOnboardingVideo();

  // No video configured → render nothing (never trap the user behind an
  // empty modal).
  if (!video) return null;

  const muxPlaybackId = video.muxPlaybackId;
  const youtubeUrl = video.youtubeUrl;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose("closed")}>
      <DialogContent className="sm:max-w-[900px] gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b border-white/10 px-5 py-4">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-300">
              Welcome to Xynth
            </span>
          </div>
          <DialogTitle className="pr-8 text-base leading-snug">
            See what a real trader did in their first weeks on Xynth
          </DialogTitle>
        </DialogHeader>
        <div className="relative aspect-video w-full bg-black">
          {muxPlaybackId ? (
            <video
              src={`https://stream.mux.com/${muxPlaybackId}/highest.mp4`}
              poster={video.thumbnailUrl ?? undefined}
              controls
              autoPlay
              playsInline
              preload="auto"
              className="absolute inset-0 h-full w-full bg-black"
            />
          ) : youtubeUrl ? (
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${youtubeUrl.split('v=')[1]?.split('&')[0]}?autoplay=1&rel=0&modestbranding=1`}
              title={video?.title ?? "Welcome to Xynth"}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              className="absolute inset-0"
            />
          ) : null}
        </div>
        <div className="flex items-center justify-between border-t border-white/10 px-5 py-3">
          <div className="flex items-center gap-3">
            <span className="text-[12px] text-white/45">
              Find this any time under the Tutorials tab.
            </span>
            {youtubeUrl && (
              <a
                href={youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[12px] text-white/55 transition-colors hover:text-white"
              >
                <ExternalLink className="h-3 w-3" />
                Watch on YouTube
              </a>
            )}
          </div>
          <button
            type="button"
            onClick={() => onClose("dont_show_again")}
            className="rounded-md px-3 py-1.5 text-[12.5px] font-medium text-white/55 transition-colors hover:bg-white/[0.06] hover:text-white"
          >
            Don&apos;t show this again
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
